/*
 * Hero scroll sequence (homepage only) — driven by Lenis's scroll event, never
 * a native scroll listener. As the user scrolls through the hero, the image
 * scales 1.0 → 1.14 and the content fades + drifts up.
 * Compositor-friendly: only transform/opacity are written, and transitions on
 * those are disabled so the JS owns each frame. Bails off the homepage, with no
 * Lenis, or under reduced motion (leaving the static Dawn hero).
 */
(function () {
  'use strict';

  if (!document.body.classList.contains('template-index')) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.elomeLenis) return;

  var banner = document.querySelector('.template-index .banner');
  if (!banner) return;
  var image = document.querySelector('.template-index .banner__media img');
  var content = document.querySelector('.template-index .banner__content');
  if (!image || !content) return;

  // Stage — Dawn already gives position:relative + isolation:isolate; ensure
  // both, and clip the scaling image with overflow:hidden.
  banner.style.position = 'relative';
  banner.style.overflow = 'hidden';
  banner.style.isolation = 'isolate';

  // Dark exit overlay — separate from Dawn's ::after legibility overlay (which
  // stays at its configured opacity for text legibility at the top).
  var overlay = document.createElement('div');
  overlay.className = 'elome-hero-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.cssText =
    'position:absolute;inset:0;background:#000;opacity:0;pointer-events:none;z-index:1;transition:none;';
  banner.appendChild(overlay);

  // Layering: image 0 < overlay 1 < content 2.
  image.style.zIndex = '0';
  content.style.zIndex = '2';

  // The JS owns transform/opacity each frame — disable transitions, and drop any
  // Dawn scroll-trigger reveal on the content (its forwards-fill animation would
  // otherwise override the inline opacity/transform written here).
  image.style.transition = 'none';
  image.style.transformOrigin = 'center center';
  image.style.willChange = 'transform';
  content.style.transition = 'none';
  content.style.willChange = 'opacity, transform';
  content.classList.remove('scroll-trigger', 'scroll-trigger--offscreen', 'animate--slide-in');

  var bannerHeight = banner.offsetHeight || window.innerHeight;
  var lastProgress = null;

  function clamp(v, min, max) {
    if (v < min) return min;
    if (v > max) return max;
    return v;
  }

  function update() {
    // Safety: if anything detached the overlay from the DOM, re-inject it.
    if (!overlay.isConnected) banner.appendChild(overlay);

    var scrollY = window.elomeLenis ? window.elomeLenis.scroll : window.pageYOffset;
    if (typeof scrollY !== 'number') scrollY = 0;

    var p = clamp(scrollY / (bannerHeight * 0.6), 0, 0.5);
    if (p === lastProgress) return;
    lastProgress = p;

    // Image scale: Phase 1 (0–0.2) 1.0→1.12, Phase 2 (0.2–0.5) 1.12→1.14,
    // Phase 3 (>0.5) holds 1.14.
    var scale;
    if (p < 0.2) scale = 1 + (p / 0.2) * 0.12;
    else if (p < 0.5) scale = 1.12 + ((p - 0.2) / 0.3) * 0.02;
    else scale = 1.14;

    // Phase 2 (0.2–0.5): content fades out + drifts up 20px. The dark overlay is
    // disabled (max opacity 0) so the hero image stays at full brightness on
    // scroll; only the content fade remains.
    var fade = clamp((p - 0.2) / 0.3, 0, 1);
    image.style.transform = 'scale(' + scale + ') translateZ(0)';
    content.style.opacity = '' + (1 - fade);
    content.style.transform = 'translateY(' + fade * -20 + 'px)';
  }

  window.elomeLenis.on('scroll', update);
  update(); // initial frame (in case the page loads mid-scroll / on bfcache)

  window.addEventListener('resize', function () {
    bannerHeight = banner.offsetHeight || window.innerHeight;
    update();
  });
})();

/*
 * Editorial panel entrance — a one-time IntersectionObserver adds
 * .elome-panel-entered as each editorial panel scrolls into view, so the CSS
 * rises + fades it in. JS-ARMED via .elome-panel-reveal so the panel is never
 * hidden when JS/IO is unavailable (no-JS shows it fully). Skipped under reduced
 * motion (CSS then shows the panel statically). The text children inside keep
 * their existing Phase 3 .elome-reveal stagger — a separate system.
 */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  function run() {
    var panels = document.querySelectorAll('.elome-editorial__panel');
    if (!panels.length) return;

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('elome-panel-entered');
          obs.unobserve(entry.target); // one-time reveal
        });
      },
      { threshold: 0.1 }
    );

    panels.forEach(function (panel) {
      panel.classList.add('elome-panel-reveal'); // arm hidden + transition state
      io.observe(panel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();

/*
 * Stories strip scroll-driven slide. Each item (<a class="elome-stories__item">
 * inside .elome-stories__strip — confirmed in sections/elome-stories-strip.liquid)
 * slides translateX(-60px) → 0 and fades 0 → 1 as the strip travels up through
 * the viewport, staggered per item. Driven entirely by Lenis's scroll event, so
 * it reverses automatically when the user scrolls back up. No CSS transition —
 * the JS writes every frame. Bails (items stay visible) without Lenis or under
 * reduced motion; the hidden initial state is JS-set so no-JS shows items.
 *
 *   progress = (windowHeight − strip.getBoundingClientRect().top) / windowHeight
 *   (≡ (scrollY − (stripTop − windowHeight)) / windowHeight), clamped 0–1.
 *   item k spans progress [k*0.1, k*0.1 + 0.4]: 0–0.4, 0.1–0.5, 0.2–0.6, 0.3–0.7.
 */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.elomeLenis) return;

  function clamp(v, min, max) {
    if (v < min) return min;
    if (v > max) return max;
    return v;
  }

  function init() {
    var strip = document.querySelector('.elome-stories__strip');
    if (!strip) return;
    var items = strip.querySelectorAll('.elome-stories__item');
    if (!items.length) return;

    // Initial hidden state (JS-set → no-JS shows items); no CSS transition since
    // the JS owns every frame.
    for (var i = 0; i < items.length; i++) {
      items[i].style.transition = 'none';
      items[i].style.transform = 'translateX(-60px)';
      items[i].style.opacity = '0';
    }

    var lastProgress = -1;

    function update() {
      var wh = window.innerHeight || 1;
      var top = strip.getBoundingClientRect().top;
      var progress = clamp((wh - top) / wh, 0, 1);
      if (progress === lastProgress) return; // skip redundant frames when far away
      lastProgress = progress;

      for (var k = 0; k < items.length; k++) {
        var t = clamp((progress - k * 0.1) / 0.4, 0, 1); // staggered window per item
        items[k].style.transform = 'translateX(' + -60 * (1 - t) + 'px)';
        items[k].style.opacity = '' + t;
      }
    }

    window.elomeLenis.on('scroll', update);
    update(); // initial frame
    window.addEventListener('resize', update);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

